import { ApiError, ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { Logger } from '@frontend/logger';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { SortingState } from '@tanstack/react-table';
import { toNumber } from 'lodash';

type ListItem = {
    id: string;
    [key: string]: any;
};

export interface ListInfo {
    isLoading: boolean;
    error: boolean;
    /** Current page results. Paginated lists keep all loaded pages in `pages`. */
    results: ListItem[] | null;
    pages: Record<number, ListItem[]> | null;
    pageCacheKey: string | null;
    count: number | null;
    selected: ListItem[];
    pageIndex: number;
    pageSize: number;
    sorting: SortingState;
    filter: ApiQueryParams<DefaultQueryParams | string | number>;
    refresh: boolean;
    activeFilters: Record<string, string[]> | null;
    /**
     * Number of entities created since the last fetch. Reactive lists surface this as a manual
     * refresh prompt instead of pulling the new rows in automatically.
     */
    pendingCreateCount: number;
    /** Ids of rows that were deleted and are still shown, greyed out, until they are removed. */
    pendingDeleteIds: string[];
    /** Set when a row changed but could not be refetched, so the list needs a manual refresh. */
    hasStaleRows: boolean;
}
export interface ListState {
    [key: string]: ListInfo;
}

const initialState: ListState = {};

export interface DefaultListValues {
    isLoading?: boolean;
    results?: ListItem[] | null;
    pages?: Record<number, ListItem[]> | null;
    pageCacheKey?: string | null;
    count?: number | null;
    selected?: ListItem[];
    pageIndex?: number;
    pageSize?: number;
    sorting?: SortingState;
    filter?: ApiQueryParams<DefaultQueryParams | string | number>;
    activeFilters?: Record<string, string[]> | null;
}

const defaultListValues = {
    isLoading: false,
    error: false,
    results: null,
    pages: null,
    pageCacheKey: null,
    count: null,
    selected: [],
    pageIndex: 0,
    pageSize: 25,
    sorting: [],
    refresh: false,
    filter: {},
    activeFilters: {},
    pendingCreateCount: 0,
    pendingDeleteIds: [],
    hasStaleRows: false
};

export const listSlice = createSlice({
    name: 'list',
    initialState,
    reducers: {
        initList(state, action: PayloadAction<{ listId: string; defaultListValues: DefaultListValues }>) {
            if (state[action.payload.listId]) {
                Logger.warn('List already initialized - skipping to prevent overwrite', { listId: action.payload.listId });
            } else {
                state[action.payload.listId] = { ...defaultListValues, ...action.payload.defaultListValues };
            }
        },
        refresh(state, action: PayloadAction<{ listId: string; refresh: boolean }>) {
            if (state[action.payload.listId]) {
                state[action.payload.listId].refresh = action.payload.refresh;
            }
        },
        clear(state, action: PayloadAction<string>) {
            if (state[action.payload]) {
                state[action.payload].results = null;
                state[action.payload].pages = null;
                state[action.payload].pageCacheKey = null;
                state[action.payload].count = null;
                state[action.payload].selected = [];
            }
        },
        changePage(state, action: PayloadAction<{ listId: string; page: number }>) {
            if (state[action.payload.listId]) {
                const cachedPage = state[action.payload.listId].pages?.[action.payload.page];
                if (state[action.payload.listId].pageIndex === action.payload.page) return;
                state[action.payload.listId].pageIndex = action.payload.page;
                state[action.payload.listId].results = cachedPage ?? state[action.payload.listId].results;
                state[action.payload.listId].isLoading = true;
            }
        },
        changePageSize(state, action: PayloadAction<{ listId: string; pageSize: number }>) {
            if (state[action.payload.listId]) {
                if (state[action.payload.listId].pageSize === action.payload.pageSize) return;
                state[action.payload.listId].pageSize = action.payload.pageSize;
                state[action.payload.listId].pageIndex = 0;
                state[action.payload.listId].pages = null;
                state[action.payload.listId].pageCacheKey = null;
                state[action.payload.listId].isLoading = true;
            }
        },
        changeSorting(state, action: PayloadAction<{ listId: string; sorting: SortingState }>) {
            if (state[action.payload.listId]) {
                if (isSameSorting(state[action.payload.listId].sorting, action.payload.sorting)) return;
                state[action.payload.listId].sorting = action.payload.sorting;
                state[action.payload.listId].pageIndex = 0;
                state[action.payload.listId].pages = null;
                state[action.payload.listId].pageCacheKey = null;
                state[action.payload.listId].isLoading = true;
            }
        },
        select(state, action: PayloadAction<{ listId: string; objId: string }>) {
            if (state[action.payload.listId]) {
                const found = state[action.payload.listId].selected.find((o) => o.id == action.payload.objId);
                if (found) {
                    state[action.payload.listId].selected = state[action.payload.listId].selected.filter((o) => o.id != action.payload.objId);
                } else {
                    if (state[action.payload.listId].results != null && state[action.payload.listId].results!.find((o) => o?.id == action.payload.objId))
                        state[action.payload.listId].selected.push(state[action.payload.listId].results!.find((o) => o?.id == action.payload.objId)!);
                }
            }
        },
        allSelect(state, action: PayloadAction<{ listId: string; index: number; size: number }>) {
            if (state[action.payload.listId] && state[action.payload.listId].results) {
                const relevantObjects = state[action.payload.listId].results!;
                if (relevantObjects.length === 0) return;
                if (relevantObjects.every((obj) => state[action.payload.listId].selected!.find((o) => o.id == obj.id) != undefined)) {
                    state[action.payload.listId].selected = removeDuplicatesById(
                        state[action.payload.listId].selected!.filter((obj) => relevantObjects.find((o) => o.id == obj.id) == undefined)
                    );
                } else {
                    state[action.payload.listId].selected = removeDuplicatesById(state[action.payload.listId].selected!.concat(relevantObjects));
                }
            }
        },
        resetSelect(state, action: PayloadAction<{ listId: string }>) {
            if (state[action.payload.listId]) {
                state[action.payload.listId].selected = [];
            }
        },
        changeFilters(
            state,
            action: PayloadAction<{ listId: string; filters: { key: DefaultQueryParams | string | number; value: string | string[] | undefined | null }[] }>
        ) {
            if (state[action.payload.listId]) {
                const changed = action.payload.filters.some((filter) => !isSameQueryValue(state[action.payload.listId].filter[filter.key], filter.value));
                if (!changed) return;

                action.payload.filters.forEach((filter) => {
                    state[action.payload.listId].filter[filter.key] = filter.value;
                });
                state[action.payload.listId].pageIndex = 0;
                state[action.payload.listId].pages = null;
                state[action.payload.listId].pageCacheKey = null;
                state[action.payload.listId].isLoading = true;
            }
        },
        changeFilter(state, action: PayloadAction<{ listId: string; key: DefaultQueryParams | string | number; value: string | string[] | undefined | null }>) {
            if (state[action.payload.listId]) {
                if (isSameQueryValue(state[action.payload.listId].filter?.[action.payload.key], action.payload.value)) return;

                if (state[action.payload.listId].filter == null) state[action.payload.listId].filter = {};
                if (action.payload.value == null) {
                    delete state[action.payload.listId].filter[action.payload.key];
                } else if (state[action.payload.listId].filter) {
                    state[action.payload.listId].filter[action.payload.key] = action.payload.value;
                } else {
                    state[action.payload.listId].filter = {
                        [action.payload.key]: action.payload.value
                    };
                }
                state[action.payload.listId].pageIndex = 0;
                state[action.payload.listId].pages = null;
                state[action.payload.listId].pageCacheKey = null;
                state[action.payload.listId].isLoading = true;
            }
        },
        clearFilter(state, action: PayloadAction<{ listId: string; defaultValue?: ApiQueryParams<DefaultQueryParams | string | number> }>) {
            if (state[action.payload.listId]) {
                const defaultValue = action.payload.defaultValue ?? {};
                if (areQueryParamsEqual(state[action.payload.listId].filter, defaultValue)) return;

                state[action.payload.listId].filter = defaultValue;
                state[action.payload.listId].pageIndex = 0;
                state[action.payload.listId].pages = null;
                state[action.payload.listId].pageCacheKey = null;
                state[action.payload.listId].isLoading = true;
            }
        },
        changeData(state, action: PayloadAction<{ listId: string; data: { [key: string]: any; id: string }[]; replace?: boolean }>) {
            if (state[action.payload.listId]) {
                if (action.payload.replace) {
                    state[action.payload.listId].results = action.payload.data;
                    state[action.payload.listId].pages = null;
                    state[action.payload.listId].pageCacheKey = null;
                    state[action.payload.listId].count = action.payload.data.length;
                    return;
                }

                if (state[action.payload.listId].pages) {
                    const dataById = new Map(action.payload.data.map((item) => [item.id, item]));
                    Object.entries(state[action.payload.listId].pages!).forEach(([page, pageResults]) => {
                        state[action.payload.listId].pages![Number(page)] = pageResults.map((item) =>
                            dataById.has(item.id) ? { ...item, ...dataById.get(item.id)! } : item
                        );
                    });
                    state[action.payload.listId].results =
                        state[action.payload.listId].pages![state[action.payload.listId].pageIndex] ?? state[action.payload.listId].results;
                    return;
                }

                const existingResults = state[action.payload.listId].results ?? [];
                const dataById = new Map(action.payload.data.map((item) => [item.id, item]));
                const updatedResults = existingResults.map((item) => (dataById.has(item.id) ? { ...item, ...dataById.get(item.id)! } : item));
                const newItems = action.payload.data.filter((item) => !existingResults.some((existing) => existing.id === item.id));
                state[action.payload.listId].results = updatedResults.concat(newItems);
                state[action.payload.listId].count = state[action.payload.listId].results!.length;
            }
        },
        addActiveFilter(state, action: PayloadAction<{ listId: string; key: string; value: string | string[] }>) {
            if (state[action.payload.listId]) {
                if (!state[action.payload.listId].activeFilters) state[action.payload.listId].activeFilters = {};

                const key = action.payload.key === 'All' ? 'search' : action.payload.key;
                if (!state[action.payload.listId].activeFilters![key]) state[action.payload.listId].activeFilters![key] = [];
                const nextValue = typeof action.payload.value === 'string' ? [action.payload.value] : action.payload.value;
                if (isSameQueryValue(state[action.payload.listId].activeFilters![key], nextValue)) return;

                state[action.payload.listId].activeFilters![key] = typeof action.payload.value === 'string' ? [action.payload.value] : action.payload.value;
                state[action.payload.listId].pageIndex = 0;
                state[action.payload.listId].pages = null;
                state[action.payload.listId].pageCacheKey = null;
                state[action.payload.listId].isLoading = true;
            }
        },
        removeActiveFilter(state, action: PayloadAction<{ listId: string; key: string; value?: string }>) {
            const list = state[action.payload.listId];
            const key = action.payload.key === 'All' ? 'search' : action.payload.key;
            if (list && list.activeFilters && list.activeFilters[key]) {
                const currentValues = list.activeFilters[key];
                if (action.payload.value === undefined) {
                    delete list.activeFilters[key];
                } else {
                    if (!currentValues.includes(action.payload.value)) return;
                    list.activeFilters[key] = list.activeFilters[key].filter((v) => v !== action.payload.value);
                    if (list.activeFilters[key].length === 0) {
                        delete list.activeFilters[key];
                    }
                }
                if (Object.keys(list.activeFilters).length === 0) {
                    list.activeFilters = null;
                }
                list.pageIndex = 0;
                list.pages = null;
                list.pageCacheKey = null;
                list.isLoading = true;
            }
        },
        resetActiveFilters(state, action: PayloadAction<{ listId: string }>) {
            const list = state[action.payload.listId];
            if (list) {
                if (list.activeFilters == null || Object.keys(list.activeFilters).length === 0) return;
                list.activeFilters = null;
                list.pageIndex = 0;
                list.pages = null;
                list.pageCacheKey = null;
                list.isLoading = true;
            }
        },
        registerPendingCreate(state, action: PayloadAction<{ listId: string }>) {
            const list = state[action.payload.listId];
            if (!list) return;
            list.pendingCreateCount = (list.pendingCreateCount ?? 0) + 1;
        },
        registerStaleRow(state, action: PayloadAction<{ listId: string }>) {
            const list = state[action.payload.listId];
            if (!list || list.hasStaleRows) return;
            list.hasStaleRows = true;
        },
        /** Dismisses the refresh prompt without refetching. */
        clearPendingCreates(state, action: PayloadAction<{ listId: string }>) {
            const list = state[action.payload.listId];
            if (!list || (!list.pendingCreateCount && !list.hasStaleRows)) return;
            list.pendingCreateCount = 0;
            list.hasStaleRows = false;
        },
        /** Merge a freshly resolved entity into the row it belongs to, leaving every other row alone. */
        patchRow(state, action: PayloadAction<{ listId: string; row: ListItem }>) {
            const list = state[action.payload.listId];
            if (!list) return;
            const { row } = action.payload;

            if (list.pages) {
                Object.keys(list.pages).forEach((page) => {
                    list.pages![Number(page)] = mergeRowIntoItems(list.pages![Number(page)], row);
                });
                list.results = list.pages[list.pageIndex] ?? (list.results ? mergeRowIntoItems(list.results, row) : list.results);
            } else if (list.results) {
                list.results = mergeRowIntoItems(list.results, row);
            }
            list.selected = mergeRowIntoItems(list.selected, row);
        },
        markRowPendingDelete(state, action: PayloadAction<{ listId: string; id: string }>) {
            const list = state[action.payload.listId];
            if (!list) return;
            if (list.pendingDeleteIds == null) list.pendingDeleteIds = [];
            if (list.pendingDeleteIds.includes(action.payload.id)) return;
            list.pendingDeleteIds.push(action.payload.id);
        },
        removeRow(state, action: PayloadAction<{ listId: string; id: string }>) {
            const list = state[action.payload.listId];
            if (!list) return;
            const { id } = action.payload;
            const without = (items: ListItem[]) => items.filter((item) => item.id !== id);
            const wasPresent =
                (list.pages ? Object.values(list.pages).some((page) => page.some((item) => item.id === id)) : false) ||
                (list.results?.some((item) => item.id === id) ?? false);

            if (list.pages) {
                Object.keys(list.pages).forEach((page) => {
                    list.pages![Number(page)] = without(list.pages![Number(page)]);
                });
                list.results = list.pages[list.pageIndex] ?? (list.results ? without(list.results) : list.results);
            } else if (list.results) {
                list.results = without(list.results);
            }
            list.selected = without(list.selected);
            list.pendingDeleteIds = (list.pendingDeleteIds ?? []).filter((pendingId) => pendingId !== id);
            if (wasPresent && list.count != null) list.count = Math.max(0, list.count - 1);

            // Without a known total we cannot tell whether the page index is still valid.
            if (list.count == null) return;

            // Keep pageIndex in range after deletes so the UI does not sit on an empty out-of-range page.
            const maxPageIndex = list.count > 0 ? Math.max(0, Math.ceil(list.count / list.pageSize) - 1) : 0;
            if (list.pageIndex > maxPageIndex) {
                list.pageIndex = maxPageIndex;
                list.results = list.pages?.[list.pageIndex] ?? list.results;
            }

            // Current page emptied but rows remain elsewhere — invalidate cache and refetch.
            const currentPageItems = list.pages?.[list.pageIndex] ?? list.results;
            if (list.count > 0 && currentPageItems != null && currentPageItems.length === 0) {
                list.pages = null;
                list.pageCacheKey = null;
                list.refresh = true;
                list.isLoading = true;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetch.pending, (state, action) => {
                if (state[action.meta.arg.key] == null) {
                    state[action.meta.arg.key] = { ...defaultListValues };
                }
                state[action.meta.arg.key].isLoading = true;
                state[action.meta.arg.key].error = false;
            })
            .addCase(fetch.rejected, (state, action) => {
                if (state[action.meta.arg.key] != null) {
                    state[action.meta.arg.key].isLoading = false;
                    state[action.meta.arg.key].error = true;
                }
            })
            .addCase(fetch.fulfilled, (state, action) => {
                if (action.meta.arg.params['index'] !== undefined && action.meta.arg.params['size'] !== undefined) {
                    const pageIndex = toNumber(action.meta.arg.params['index']);
                    const pageCacheKey = getPageCacheKey(action.meta.arg.params);
                    if (state[action.meta.arg.key] == null) {
                        //shouldnt happen if init function was called
                        state[action.meta.arg.key] = { ...defaultListValues };
                    }

                    const list = state[action.meta.arg.key];
                    const pages = list.pageCacheKey === pageCacheKey ? { ...(list.pages ?? {}) } : {};
                    pages[pageIndex] = action.payload.results;
                    list.count = action.payload.count;
                    list.results = action.payload.results;
                    list.pages = pages;
                    list.pageCacheKey = pageCacheKey;
                } else {
                    state[action.meta.arg.key] = { ...defaultListValues, ...action.payload, pages: null, pageCacheKey: null };
                }
                const list = state[action.meta.arg.key];
                list.isLoading = false;
                list.error = false;
                // A completed fetch already reflects every change we were tracking. Only touch these
                // when they are set, so we do not replace array references and re-render every row.
                if (list.pendingCreateCount) list.pendingCreateCount = 0;
                if (list.hasStaleRows) list.hasStaleRows = false;
                if (list.pendingDeleteIds?.length) list.pendingDeleteIds = [];
            });
    }
});

type Options = {
    key: string;
    params: ApiQueryParams<DefaultQueryParams | string | number>;
    func: (queryParams?: ApiQueryParams<DefaultQueryParams | string | number>) => Promise<PaginatedResponse<any>>;
};
export const fetch = createAsyncThunk<PaginatedResponse<any>, Options>('fetch', async (options: Options, { rejectWithValue }) => {
    try {
        return await options.func(options.params);
    } catch (e) {
        if ((e as ApiError).json) return rejectWithValue(e);
        throw e;
    }
});

export const listStore = { lists: listSlice.reducer };
export const {
    clear,
    initList,
    refresh,
    changePage,
    changePageSize,
    changeSorting,
    select,
    allSelect,
    resetSelect,
    changeFilter,
    changeFilters,
    clearFilter,
    changeData,
    addActiveFilter,
    removeActiveFilter,
    resetActiveFilters,
    registerPendingCreate,
    registerStaleRow,
    clearPendingCreates,
    patchRow,
    markRowPendingDelete,
    removeRow
} = listSlice.actions;

function rowsAreEqual(a: ListItem, b: ListItem): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
        if ((a as Record<string, unknown>)[key] !== (b as Record<string, unknown>)[key]) return false;
    }
    return true;
}

/** Merges `row` into the matching item; returns the same array reference when nothing changed. */
function mergeRowIntoItems(items: ListItem[], row: ListItem): ListItem[] {
    let changed = false;
    const next = items.map((item) => {
        if (item.id !== row.id) return item;
        const merged = { ...item, ...row };
        if (rowsAreEqual(item, merged)) return item;
        changed = true;
        return merged;
    });
    return changed ? next : items;
}

function removeDuplicatesById<T extends { id: string }>(arr: T[]): T[] {
    const seenIds = new Set();
    const uniqueArr: T[] = [];

    for (const item of arr) {
        if (!seenIds.has(item.id)) {
            seenIds.add(item.id);
            uniqueArr.push(item);
        }
    }

    return uniqueArr;
}

function getPageCacheKey(params: ApiQueryParams<DefaultQueryParams | string | number>): string {
    const { index, ...cacheParams } = params;
    return JSON.stringify(cacheParams);
}

function isSameSorting(current: SortingState, next: SortingState): boolean {
    if (current.length !== next.length) return false;
    return current.every((sort, index) => sort.id === next[index]?.id && sort.desc === next[index]?.desc);
}

function areQueryParamsEqual(
    current: ApiQueryParams<DefaultQueryParams | string | number>,
    next: ApiQueryParams<DefaultQueryParams | string | number>
): boolean {
    const keys = new Set([...Object.keys(current), ...Object.keys(next)]);
    return Array.from(keys).every((key) => isSameQueryValue(current[key], next[key]));
}

function isSameQueryValue(current: string | string[] | undefined | null, next: string | string[] | undefined | null): boolean {
    if (current == null && next == null) return true;
    if (Array.isArray(current) || Array.isArray(next)) {
        if (!Array.isArray(current) || !Array.isArray(next) || current.length !== next.length) return false;
        return current.every((value, index) => value === next[index]);
    }
    return current === next;
}
